import { useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { InView } from 'react-intersection-observer'

import Link from '@/components/common/Link'
import LangLink from '@/components/common/LangLink'
import { getNavigationIcon } from '@/constants/navigationAssets'
import { useFetchCustomizedClients } from '@/service/apiHooks/useFetchCustomizedClients'
import { useNavigationTracking } from '@/hooks/useNavigationTracking'

import FaqSubmenu from './FaqSubmenu'
import ParkingsSubmenu from './ParkingsSubmenu'
import AirportsSubmenu from './AirportsSubmenu'
import {
  dropdownIcon,
  dropdownIconActive,
  link,
  linksList,
  linksListItem,
  linksListItemWithDropdown,
  onlyDesktop,
  onlyMobile
} from './Links.styled'

interface Props {
  short?: boolean
  onClick?(): void
}

export default function Links({ short = false, onClick }: Props) {
  const { t } = useTranslation('common')
  const { pathname, query } = useRouter()
  const { id } = query as any
  const { clientDetails } = useFetchCustomizedClients({ id })
  const { trackClick } = useNavigationTracking()
  const [hoverFaq, setHoverFaq] = useState(false)
  const [clickFaq, setClickFaq] = useState(false)
  const [hoverPark, setHoverPark] = useState(false)
  const [clickPark, setClickPark] = useState(false)
  const [hoverAir, setHoverAir] = useState(false)
  const [clickAir, setClickAir] = useState(false)

  const handleClick = (linkName: string) => {
    trackClick(`menu_navigation_${linkName}`)
    onClick?.()
  }

  return (
    <>
      <ul css={linksList}>
        {short && !clientDetails?.free && (
          <li css={linksListItem}>
            <Link href={{ pathname: '/' }} passHref prefetch={false}>
              <a
                onClick={() => handleClick('home_main')}
                css={[link.linkItem, pathname === '/' && link.linkItemActive]}
              >
                {t('navigation.home')}
              </a>
            </Link>
          </li>
        )}
        {!short && (
          <>
            <li
              css={[linksListItem, linksListItemWithDropdown]}
              onMouseEnter={() => setHoverPark(true)}
              onMouseLeave={() => setHoverPark(false)}
              data-test-id="menu-parkings"
            >
              <button
                onClick={() => {
                  setClickPark(!clickPark)
                  handleClick('search_parkings')
                }}
                css={link.linkItem}
              >
                {t('navigation.search')}
                <img
                  src={getNavigationIcon('smallDropdown')}
                  alt="Dropdown"
                  css={[
                    dropdownIcon,
                    (hoverPark || clickPark) && dropdownIconActive
                  ]}
                />
              </button>
              <InView triggerOnce rootMargin="200px">
                {({ ref, inView }) => (
                  <div ref={ref}>
                    {inView && (
                      <ParkingsSubmenu hover={hoverPark} click={clickPark} />
                    )}
                  </div>
                )}
              </InView>
            </li>
            <li
              css={[linksListItem, linksListItemWithDropdown]}
              onMouseEnter={() => setHoverAir(true)}
              onMouseLeave={() => setHoverAir(false)}
              data-test-id="menu-park-and-fly"
            >
              <button
                onClick={() => {
                  setClickAir(!clickAir)
                  handleClick('search_airports')
                }}
                css={link.linkItem}
              >
                {t('navigation.parkAndFly')}
                <img
                  src={getNavigationIcon('smallDropdown')}
                  alt="Dropdown"
                  css={[
                    dropdownIcon,
                    (hoverAir || clickAir) && dropdownIconActive
                  ]}
                />
              </button>
              <InView triggerOnce rootMargin="200px">
                {({ ref, inView }) => (
                  <div ref={ref}>
                    {inView && (
                      <AirportsSubmenu hover={hoverAir} click={clickAir} />
                    )}
                  </div>
                )}
              </InView>
            </li>
            <li css={linksListItem} data-test-id="menu-navipay">
              <Link href={{ pathname: '/navipay' }} passHref prefetch={false}>
                <a
                  onClick={() => handleClick('navipay_section')}
                  css={[
                    link.linkItem,
                    pathname === '/navipay' && link.linkItemActive
                  ]}
                >
                  {t('navigation.navipay')}
                </a>
              </Link>
            </li>
            <li
              css={[linksListItem, linksListItemWithDropdown]}
              onMouseEnter={() => setHoverFaq(true)}
              onMouseLeave={() => setHoverFaq(false)}
              data-test-id="menu-faq"
            >
              <button
                onClick={() => {
                  setClickFaq(!clickFaq)
                  handleClick('faq_main')
                }}
                css={[
                  link.linkItem,
                  (pathname === '/faq' || pathname.startsWith('/faq/')) &&
                    link.linkItemActive
                ]}
              >
                {t('navigation.faq')}
                <img
                  src={getNavigationIcon('smallDropdown')}
                  alt="Dropdown"
                  css={[
                    dropdownIcon,
                    (hoverFaq || clickFaq) && dropdownIconActive
                  ]}
                />
              </button>
              <InView triggerOnce rootMargin="200px">
                {({ ref, inView }) => (
                  <div ref={ref}>
                    {inView && (
                      <FaqSubmenu
                        onClick={() => handleClick('faq_submenu')}
                        hover={hoverFaq}
                        click={clickFaq}
                      />
                    )}
                  </div>
                )}
              </InView>
            </li>
            <li css={linksListItem} data-test-id="menu-contact">
              <Link href={{ pathname: '/contact' }} passHref prefetch={false}>
                <a
                  onClick={() => handleClick('contact_section')}
                  css={[
                    link.linkItem,
                    pathname === '/contact' && link.linkItemActive
                  ]}
                >
                  {t('navigation.contact')}
                </a>
              </Link>
            </li>
          </>
        )}
        <li css={[linksListItem, onlyDesktop]} data-test-id="menu-lang-link">
          <LangLink short={short} />
        </li>
      </ul>
      <div css={onlyMobile}>
        <LangLink short={short} />
      </div>
    </>
  )
}
